import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { AxiosError } from 'axios';
import { CatalogSelectField, CharacterCount, FormDialog, TextField } from '@/shared/ui';
import { toast } from '@/shared/feedback/toast';
import { usePermission } from '@/shared/hooks/usePermission';
import { queryKeys } from '@/shared/query';
import { treatmentUomAPI } from '../api/treatmentUom.api';

const UOM_NAME_MAX_LENGTH = 100;

interface TreatmentUomFieldProps {
  value: string;
  onChange: (uomCode: string) => void;
  error?: string;
  required?: boolean;
  disabled?: boolean;
  /** Keep an inactive UOM option visible when editing an existing item. */
  includeUomCode?: string;
}

const getErrorMessage = (err: unknown, fallback: string): string =>
  err instanceof AxiosError && err.response?.data?.message
    ? String(err.response.data.message)
    : fallback;

/**
 * Select đơn vị tính dịch vụ — quick create «Thêm đơn vị tính» neo cuối menu.
 */
const TreatmentUomField: React.FC<TreatmentUomFieldProps> = ({
  value,
  onChange,
  error,
  required = false,
  disabled = false,
  includeUomCode,
}) => {
  const { t } = useTranslation(['treatment', 'common']);
  const { hasPermission } = usePermission();
  const queryClient = useQueryClient();
  const canCreate = hasPermission('treatment-catalog:create') && !disabled;

  const [dialogOpen, setDialogOpen] = useState(false);
  const [newName, setNewName] = useState('');

  const uomsQuery = useQuery({
    queryKey: queryKeys.treatmentCatalog.uoms({ includeInactive: Boolean(includeUomCode) }),
    queryFn: async () => {
      const res = await treatmentUomAPI.getAll(Boolean(includeUomCode));
      return res.data ?? [];
    },
  });

  const uoms = (uomsQuery.data ?? []).filter(
    (uom) => uom.isActive || uom.code === includeUomCode
  );

  const options = useMemo(
    () =>
      uoms.map((uom) => ({
        value: uom.code,
        label: !uom.isActive
          ? `${uom.name} (${t('uomCatalog.status.inactive')})`
          : uom.name,
      })),
    [uoms, t]
  );

  useEffect(() => {
    if (!dialogOpen) return;
    setNewName('');
  }, [dialogOpen]);

  const createMutation = useMutation({
    mutationFn: (name: string) => treatmentUomAPI.create({ name }),
    onSuccess: async (res) => {
      const created = res.data;
      toast.success(t('uomCatalog.toasts.created'));
      setDialogOpen(false);
      setNewName('');
      await queryClient.invalidateQueries({ queryKey: queryKeys.treatmentCatalog.all });
      if (created?.code) {
        onChange(created.code);
      }
    },
    onError: (err) => {
      toast.error(getErrorMessage(err, t('uomCatalog.toasts.saveFailed')));
    },
  });

  const busy = createMutation.isPending;

  const handleCreate = (): void => {
    const trimmed = newName.trim();
    if (!trimmed) {
      toast.error(t('uomCatalog.validation.nameRequired'));
      return;
    }
    createMutation.mutate(trimmed);
  };

  return (
    <>
      <CatalogSelectField
        value={value}
        onChange={onChange}
        options={options}
        label={t('fields.uom')}
        placeholder={t('catalog.selectUom')}
        required={required}
        disabled={disabled}
        error={error}
        quickCreate={
          canCreate
            ? {
                canCreate: true,
                createLabel: t('uomCatalog.actions.addQuick'),
                onQuickCreate: () => setDialogOpen(true),
              }
            : undefined
        }
      />

      <FormDialog
        open={dialogOpen}
        title={t('uomCatalog.dialogs.addTitle')}
        busy={busy}
        onClose={() => setDialogOpen(false)}
        minHeight={200}
        maxWidth="xs"
        onSubmit={handleCreate}
        submitLabel={t('common:save')}
        busySubmitLabel={t('common:saving')}
      >
        <TextField
          autoFocus
          fullWidth
          required
          label={t('uomCatalog.form.name')}
          placeholder={t('uomCatalog.quickCreate.namePlaceholder')}
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          disabled={busy}
          labelEnd={<CharacterCount current={newName.length} max={UOM_NAME_MAX_LENGTH} />}
          slotProps={{ htmlInput: { maxLength: UOM_NAME_MAX_LENGTH } }}
        />
      </FormDialog>
    </>
  );
};

export default TreatmentUomField;
